import type { MonthGroup } from '@/cmsBackend/transformers/types/featureAnnouncement';
import { useEffect, useRef, useState } from 'react';
import { fetchProductUpdatesSlugResolution } from './fetchProductUpdatesSlugResolution';
import {
  getProductUpdatesHashSlug,
  isSlugInMonthGroups,
  scrollToAnnouncementSlug,
} from './productUpdatesHashDeepLink';

/** Max automatic load-more calls while resolving a hash (tier-1/2 slugs in archive). */
export const MAX_AUTO_LOAD_MORE_ATTEMPTS = 24;

/** Max resolve-slug API retries per hash before giving up (transient errors only). */
export const MAX_SLUG_RESOLVE_ATTEMPTS = 3;

export type SlugExistenceStatus = 'pending' | 'exists' | 'missing';

export interface UseProductUpdatesHashDeepLinkParams {
  monthGroups: MonthGroup[];
  hasMore: boolean;
  locale: string;
  /** Loads the next month; returns true when a month was appended. */
  loadMoreMonth: () => Promise<boolean>;
}

/**
 * Resolves `/product-updates#<slug>` by scrolling when the target is in the DOM,
 * or fetching older months until found or exhausted.
 */
export function useProductUpdatesHashDeepLink({
  monthGroups,
  hasMore,
  locale,
  loadMoreMonth,
}: UseProductUpdatesHashDeepLinkParams): void {
  const resolvedSlugRef = useRef<string | null>(null);
  const autoLoadAttemptsRef = useRef(0);
  const resolveAttemptsRef = useRef(0);
  const autoLoadInFlightRef = useRef(false);
  const slugForAttemptsRef = useRef<string | null>(null);
  const [hashSlug, setHashSlug] = useState<string | null>(() => getProductUpdatesHashSlug());
  const [slugExistenceBySlug, setSlugExistenceBySlug] = useState<
    Record<string, SlugExistenceStatus>
  >({});

  useEffect(() => {
    const onHashChange = () => {
      const slug = getProductUpdatesHashSlug();
      setHashSlug(slug);
      resolvedSlugRef.current = null;
      autoLoadAttemptsRef.current = 0;
      resolveAttemptsRef.current = 0;
      autoLoadInFlightRef.current = false;
      slugForAttemptsRef.current = null;
      setSlugExistenceBySlug({});
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const slug = hashSlug;
    if (!slug || resolvedSlugRef.current === slug) {
      return;
    }

    if (slugForAttemptsRef.current !== slug) {
      slugForAttemptsRef.current = slug;
      autoLoadAttemptsRef.current = 0;
      resolveAttemptsRef.current = 0;
    }

    if (scrollToAnnouncementSlug(slug)) {
      resolvedSlugRef.current = slug;
      return;
    }

    if (isSlugInMonthGroups(slug, monthGroups)) {
      const frameId = requestAnimationFrame(() => {
        if (scrollToAnnouncementSlug(slug)) {
          resolvedSlugRef.current = slug;
        }
      });
      return () => cancelAnimationFrame(frameId);
    }

    const existenceStatus = slugExistenceBySlug[slug];

    if (existenceStatus === undefined) {
      setSlugExistenceBySlug((prev) => ({ ...prev, [slug]: 'pending' }));

      const handleResolveFailure = () => {
        resolveAttemptsRef.current += 1;
        if (resolveAttemptsRef.current >= MAX_SLUG_RESOLVE_ATTEMPTS) {
          resolvedSlugRef.current = slug;
        }
        setSlugExistenceBySlug((prev) => {
          const next = { ...prev };
          delete next[slug];
          return next;
        });
      };

      void fetchProductUpdatesSlugResolution(slug, locale)
        .then((result) => {
          if (result.status === 'error') {
            handleResolveFailure();
            return;
          }
          setSlugExistenceBySlug((prev) => ({
            ...prev,
            [slug]: result.status === 'exists' ? 'exists' : 'missing',
          }));
        })
        .catch(handleResolveFailure);
      return;
    }

    if (existenceStatus === 'pending') {
      return;
    }

    if (existenceStatus === 'missing') {
      resolvedSlugRef.current = slug;
      return;
    }

    if (autoLoadInFlightRef.current) {
      return;
    }

    if (!hasMore || autoLoadAttemptsRef.current >= MAX_AUTO_LOAD_MORE_ATTEMPTS) {
      resolvedSlugRef.current = slug;
      return;
    }

    autoLoadInFlightRef.current = true;
    void loadMoreMonth()
      .then((appended) => {
        if (appended) {
          autoLoadAttemptsRef.current += 1;
        }
      })
      .finally(() => {
        autoLoadInFlightRef.current = false;
      });
  }, [monthGroups, hasMore, loadMoreMonth, locale, slugExistenceBySlug, hashSlug]);
}
